// Lyrics parsing utilities
interface Word {
  type: string;
  text: string;
  pokemon?: string | null;
  lineIndex?: number;
}

export interface TimedLine {
  time: number; // time in seconds
  text: string;
  lineIndex: number;
}

export function parseLrcFile(lrcContent: string): TimedLine[] {
  const lines = lrcContent.split('\n');
  const timedLines: TimedLine[] = [];
  
  console.log('Parsing LRC file with', lines.length, 'lines');
  
  lines.forEach((line, index) => {
    const trimmedLine = line.trim();
    
    if (!trimmedLine) {
      // Add empty line with music symbols on one line
      timedLines.push({
        time: 0, // Will be adjusted later if needed
        text: "♪ ♫ ♪", // Music symbols on one line
        lineIndex: index
      });
      return;
    }
    
    // Parse LRC format: [mm:ss.xx]lyrics text
    const match = trimmedLine.match(/^\[(\d{2}):(\d{2})\.(\d{2})\](.*)$/);
    if (match) {
      const minutes = parseInt(match[1], 10);
      const seconds = parseInt(match[2], 10);
      const centiseconds = parseInt(match[3], 10);
      const time = minutes * 60 + seconds + centiseconds / 100;
      const text = match[4].trim(); // Trim whitespace from text
      
      // If text is empty after trimming, treat as music symbols
      if (!text) {
        timedLines.push({
          time,
          text: "♪ ♫ ♪",
          lineIndex: index
        });
      } else {
        timedLines.push({
          time,
          text,
          lineIndex: index
        });
      }
      
      // Debug first few lines
      if (index < 3) {
        console.log(`Parsed line ${index}: time=${time}, text="${text || '♪ ♫ ♪'}"`);
      }
    } else {
      console.log(`Failed to parse line ${index}: "${trimmedLine}"`);
    }
  });
  
  console.log('Successfully parsed', timedLines.length, 'timed lines');
  return timedLines;
}

export function parseLyrics(
  lyrics: string,
  mappings: Map<string, string>,
  caseInsensitive: boolean,
  timedLines?: TimedLine[],
): Word[] {
  const lines = lyrics.split("\n");
  const words: Word[] = [];

  lines.forEach((line, lineIndex) => {
    if (!line.trim()) {
      // Add music symbols as one combined word for empty lines
      words.push({ 
        type: "word", 
        text: "♪ ♫ ♪", 
        pokemon: mappings.get("♪ ♫ ♪".toLowerCase()) || "pokeflute",
        lineIndex 
      });
      words.push({ type: "newline", text: "", pokemon: null });
      return;
    }

    const lineWords = parseLine(line, mappings, caseInsensitive);
    words.push(...lineWords);
    words.push({ type: "newline", text: "", pokemon: null });
  });

  return words;
}

export function parseTimedLyrics(
  timedLines: TimedLine[],
  mappings: Map<string, string>,
  caseInsensitive: boolean,
): Word[] {
  const words: Word[] = [];

  timedLines.forEach((timedLine, lineIndex) => {
    if (!timedLine.text.trim() || timedLine.text === "♪ ♫ ♪") {
      // Add music symbols as one combined word for empty lines
      words.push({ 
        type: "word", 
        text: "♪ ♫ ♪", 
        pokemon: mappings.get("♪ ♫ ♪".toLowerCase()) || "pokeflute",
        lineIndex 
      });
      words.push({ type: "newline", text: "", pokemon: null, lineIndex });
      return;
    }

    const lineWords = parseLine(timedLine.text, mappings, caseInsensitive);
    // Add line index to each word
    const wordsWithLineIndex = lineWords.map(word => ({ ...word, lineIndex }));
    words.push(...wordsWithLineIndex);
    words.push({ type: "newline", text: "", pokemon: null, lineIndex });
  });

  return words;
}

function parseLine(
  line: string,
  mappings: Map<string, string>,
  caseInsensitive: boolean,
): Word[] {
  const words: Word[] = [];
  let remainingLine = line;

  // Sort mappings by length (longest first) to prioritize longer phrases
  const sortedMappings = Array.from(mappings.keys()).sort(
    (a, b) => b.length - a.length,
  );

  while (remainingLine.trim() !== "") {
    let matched = false;

    // Check each mapping to see if it exists at the start of remaining line
    for (const mappingKey of sortedMappings) {
      const lineToCheck = caseInsensitive
        ? remainingLine.toLowerCase()
        : remainingLine;
      const mappingToCheck = caseInsensitive
        ? mappingKey.toLowerCase()
        : mappingKey;

      // Look for the mapping followed by whitespace or end of string
      // Only split on punctuation if the punctuation itself is mapped
      const regex = new RegExp(`^${escapeRegex(mappingToCheck)}(\\s|$)`, "i");
      if (regex.test(lineToCheck)) {
        const match = remainingLine.match(
          new RegExp(`^${escapeRegex(mappingKey)}`, "i"),
        );
        if (match) {
          words.push({
            type: "word",
            text: match[0],
            pokemon: mappings.get(mappingKey),
          });
          remainingLine = remainingLine.substring(match[0].length).trim();
          matched = true;
          break;
        }
      }

      // Check for mappings followed by mapped punctuation (like "September?" where ? is mapped)
      const mappingWithPunctuationRegex = new RegExp(
        `^${escapeRegex(mappingToCheck)}[^\\w\\s]+`,
        "i",
      );
      if (mappingWithPunctuationRegex.test(lineToCheck)) {
        const match = remainingLine.match(
          new RegExp(`^${escapeRegex(mappingKey)}[^\\w\\s]+`, "i"),
        );
        if (match) {
          const fullMatch = match[0];
          const mappingPart = mappingKey;
          const punctuationPart = fullMatch.substring(mappingKey.length);
          
          // Only split if the punctuation is actually mapped
          if (mappings.has(punctuationPart)) {
            // Add the mapping part
            words.push({
              type: "word",
              text: mappingPart,
              pokemon: mappings.get(mappingKey),
            });
            
            // Add the punctuation part (mapped)
            words.push({
              type: "word",
              text: punctuationPart,
              pokemon: mappings.get(punctuationPart),
            });
            
            remainingLine = remainingLine.substring(fullMatch.length).trim();
            matched = true;
            break;
          }
        }
      }

      // Check for mappings surrounded by punctuation (like "(dee-ya,")
      const punctuationSurroundedRegex = new RegExp(
        `^[^\\w\\s]+${escapeRegex(mappingToCheck)}[^\\w\\s]+`,
        "i",
      );
      if (punctuationSurroundedRegex.test(lineToCheck)) {
        const match = remainingLine.match(
          new RegExp(`^[^\\w\\s]+${escapeRegex(mappingKey)}[^\\w\\s]+`, "i"),
        );
        if (match) {
          words.push({
            type: "word",
            text: match[0],
            pokemon: mappings.get(mappingKey),
          });
          remainingLine = remainingLine.substring(match[0].length).trim();
          matched = true;
          break;
        }
      }

      // Check for mappings preceded by punctuation (like "(dee-ya" or "(dee-ya,")
      const punctuationPrecededRegex = new RegExp(
        `^[^\\w\\s]+${escapeRegex(mappingToCheck)}`,
        "i",
      );
      if (punctuationPrecededRegex.test(lineToCheck)) {
        const match = remainingLine.match(
          new RegExp(`^[^\\w\\s]+${escapeRegex(mappingKey)}[^\\w\\s]*`, "i"),
        );
        if (match) {
          words.push({
            type: "word",
            text: match[0],
            pokemon: mappings.get(mappingKey),
          });
          remainingLine = remainingLine.substring(match[0].length).trim();
          matched = true;
          break;
        }
      }

      // Check for mappings followed by punctuation (like "dee-ya,")
      const punctuationFollowedRegex = new RegExp(
        `^${escapeRegex(mappingToCheck)}[^\\w\\s]+`,
        "i",
      );
      if (punctuationFollowedRegex.test(lineToCheck)) {
        const match = remainingLine.match(
          new RegExp(`^${escapeRegex(mappingKey)}[^\\w\\s]+`, "i"),
        );
        if (match) {
          words.push({
            type: "word",
            text: match[0],
            pokemon: mappings.get(mappingKey),
          });
          remainingLine = remainingLine.substring(match[0].length).trim();
          matched = true;
          break;
        }
      }

      // Also check for apostrophe variations
      // This allows "changin" to match "changin'" in the lyrics
      if (mappingKey.includes("'") || remainingLine.includes("'")) {
        const mappingWithoutApostrophe = mappingKey.replace(/'/g, '');
        const lineWithoutApostrophe = remainingLine.replace(/'/g, '');
        const lineToCheckNoApostrophe = caseInsensitive
          ? lineWithoutApostrophe.toLowerCase()
          : lineWithoutApostrophe;
        const mappingToCheckNoApostrophe = caseInsensitive
          ? mappingWithoutApostrophe.toLowerCase()
          : mappingWithoutApostrophe;

        const regexNoApostrophe = new RegExp(`^${escapeRegex(mappingToCheckNoApostrophe)}(\\s|$)`, "i");
        if (regexNoApostrophe.test(lineToCheckNoApostrophe)) {
          // Find the original text that matches (preserving original formatting)
          // For mappings without apostrophes, create a regex that allows optional apostrophes
          let originalMatch;
          if (!mappingKey.includes("'")) {
            // Mapping is "changin", but we want to match "changin'" in lyrics
            originalMatch = remainingLine.match(
              new RegExp(`^${escapeRegex(mappingKey)}'?`, "i"),
            );
          } else {
            // Mapping has apostrophes, match exactly
            originalMatch = remainingLine.match(
              new RegExp(`^${escapeRegex(mappingKey)}`, "i"),
            );
          }
          
          if (originalMatch) {
            // Use the original text from the lyrics, not the mapping key
            const originalText = originalMatch[0];
            words.push({
              type: "word",
              text: originalText,
              pokemon: mappings.get(mappingKey),
            });
            remainingLine = remainingLine.substring(originalText.length).trim();
            matched = true;
            break;
          }
        }
      }

      // Also check if the mapping is followed by mapped punctuation
      const punctuationRegex = new RegExp(
        `^${escapeRegex(mappingToCheck)}([\\p{P}\\p{S}]+)`,
        "iu",
      );
      const punctuationMatch = remainingLine.match(punctuationRegex);
      if (punctuationMatch) {
        const punctuation = punctuationMatch[1];
        // Check if any of the punctuation characters are mapped
        const hasMappedPunctuation = Array.from(punctuation).some((char) =>
          mappings.has(char),
        );
        if (hasMappedPunctuation) {
          // Split only if punctuation is mapped
          const match = remainingLine.match(
            new RegExp(`^${escapeRegex(mappingKey)}`, "i"),
          );
          if (match) {
            words.push({
              type: "word",
              text: match[0],
              pokemon: mappings.get(mappingKey),
            });
            remainingLine = remainingLine.substring(match[0].length).trim();
            matched = true;
            break;
          }
        }
      }
    }

    if (!matched) {
      // No mapping found, take the first word including any trailing punctuation
      const match = remainingLine.match(/^(\S+)/);
      if (match) {
        const firstWord = match[1];

        // Check if this word ends with punctuation that's not in our mappings
        const punctuationMatch = firstWord.match(/^(.+?)([^\w\s]+)$/);
        if (punctuationMatch) {
          const wordPart = punctuationMatch[1];
          const punctuationPart = punctuationMatch[2];

          // If the punctuation isn't mapped, keep it with the word
          if (!mappings.has(punctuationPart)) {
            // Check if the word part (without punctuation) has a mapping
            const wordMapping = findWordMapping(
              wordPart,
              mappings,
              caseInsensitive,
            );
            words.push({
              type: "word",
              text: firstWord,
              pokemon: wordMapping,
            });
            remainingLine = remainingLine.substring(firstWord.length).trim();
          } else {
            // If punctuation is mapped, split them
            const wordMapping = findWordMapping(
              wordPart,
              mappings,
              caseInsensitive,
            );
            if (wordPart) {
              words.push({
                type: "word",
                text: wordPart,
                pokemon: wordMapping,
              });
            }
            words.push({
              type: "word",
              text: punctuationPart,
              pokemon: mappings.get(punctuationPart),
            });
            remainingLine = remainingLine.substring(firstWord.length).trim();
          }
        } else {
          // No trailing punctuation, check if the word has a mapping
          const wordMapping = findWordMapping(
            firstWord,
            mappings,
            caseInsensitive,
          );
          words.push({
            type: "word",
            text: firstWord,
            pokemon: wordMapping,
          });
          remainingLine = remainingLine.substring(firstWord.length).trim();
        }
      } else {
        // Handle any remaining characters
        remainingLine = remainingLine.substring(1).trim();
      }
    }
  }

  return words;
}

function escapeRegex(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function findWordMapping(
  word: string,
  mappings: Map<string, string>,
  caseInsensitive: boolean,
): string | null {
  // Check for exact match first
  if (mappings.has(word)) {
    return mappings.get(word) || null;
  }

  // Check for case-insensitive match if enabled
  if (caseInsensitive) {
    const lowerWord = word.toLowerCase();
    for (const [key, value] of Array.from(mappings.entries())) {
      if (key.toLowerCase() === lowerWord) {
        return value;
      }
    }
  }

  // Check for match with apostrophe normalization
  // This allows "changin" to match "changin'" and vice versa
  const wordWithoutApostrophe = word.replace(/'/g, '');
  const lowerWordWithoutApostrophe = wordWithoutApostrophe.toLowerCase();
  
  for (const [key, value] of Array.from(mappings.entries())) {
    const keyWithoutApostrophe = key.replace(/'/g, '');
    if (keyWithoutApostrophe.toLowerCase() === lowerWordWithoutApostrophe) {
      return value;
    }
  }

  return null;
}
